import { useCallback } from 'react';

import { useDrop } from 'react-dnd';

import { cn } from '@/lib/utils';

import styles from './ReorderPlaceholder.module.scss';

type DragItem = { name: string; index: number; type: string } & (
  { nodeId: string; actionId?: never } | { actionId: string; nodeId?: never }
);

type PlaceholderProps = {
  label?: string;
  accept: string;
  index?: number;
  onMove: (item: DragItem, index: number) => void;
} & (
  | { type: 'condition'; position: string; isCollapsed: boolean; nodeId: string; actionId?: never }
  | { type: 'option'; position: string; actionId: string; isCollapsed?: never; nodeId?: never }
);

function ReorderPlaceholder({
  label = null,
  accept,
  index = undefined,
  onMove,
  position,
  isCollapsed,
  type,
  nodeId,
  actionId,
}: PlaceholderProps) {
  const dropNode = useCallback(
    (item: DragItem) => {
      onMove(item, index);
    },
    [index, onMove]
  );

  const [{ draggedItem, isOver, canDrop }, drop] = useDrop({
    accept,
    canDrop: (item) => {
      switch (type) {
        case 'condition':
          return item.index !== index && item.nodeId === nodeId;
        case 'option':
          return item.index !== index && item.actionId === actionId;
      }
    },
    drop: dropNode,
    collect: (monitor) => ({
      draggedItem: monitor.getItem(),
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const pos = index < draggedItem?.index ? 'left' : 'right';

  return (
    <div
      ref={(node) => {
        drop(node);
      }}
      className={cn({
        [styles.placeholder]: true,
        [styles[`placeholder--${pos}`]]: true,
        [styles['placeholder--collapsed']]: isCollapsed,
        [styles[`placeholder--over`]]: isOver && pos === position,
        [styles[`placeholder--can-drop`]]: canDrop && pos === position,
      })}
    >
      {label && <p className="text-sm leading-5 font-semibold text-informational">{label}</p>}
    </div>
  );
}

export default ReorderPlaceholder;
